import Axios from "axios";
import { toast } from "react-toastify";
import { mask } from "remask";

/**
 * Remove tudo que não for dígito.
 * @param {unknown} value
 * @returns {string}
 */
function onlyDigits(value) {
  return String(value ?? "").replace(/\D/g, "");
}

/**
 * Completa com zeros à esquerda.
 * @param {string} digits
 * @param {number} size
 * @returns {string}
 */
function padLeftZeros(digits, size) {
  return String(digits ?? "").padStart(size, "0");
}

/**
 * Valida CPF (exige EXATAMENTE 11 dígitos).
 * @param {string} digits11
 * @returns {boolean}
 */
function validaCpfDigits(digits11) {
  if (!/^\d{11}$/.test(digits11)) return false;
  if (/^(\d)\1{10}$/.test(digits11)) return false;

  const nums = digits11.split("").map((d) => Number(d));

  const calc = (baseLen) => {
    let sum = 0;
    for (let i = 0; i < baseLen; i++) {
      sum += nums[i] * ((baseLen + 1) - i);
    }
    const mod = sum % 11;
    return mod < 2 ? 0 : (11 - mod);
  };

  const d1 = calc(9);
  const d2 = calc(10);

  return nums[9] === d1 && nums[10] === d2;
}

/**
 * Valida CNPJ (exige EXATAMENTE 14 dígitos).
 * @param {string} digits14
 * @returns {boolean}
 */
function validaCnpjDigits(digits14) {
  if (!/^\d{14}$/.test(digits14)) return false;
  if (/^(\d)\1{13}$/.test(digits14)) return false;

  const nums = digits14.split("").map((d) => Number(d));

  const calc = (base, weights) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      sum += base[i] * weights[i];
    }
    const mod = sum % 11;
    return mod < 2 ? 0 : (11 - mod);
  };

  const base12 = nums.slice(0, 12);
  const d1 = calc(base12, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  const base13 = base12.concat(d1);
  const d2 = calc(base13, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  return nums[12] === d1 && nums[13] === d2;
}

/**
 * Normaliza CPF/CNPJ para não perder zeros à esquerda (Excel).
 *
 * Regras:
 * - Se tiver 9..11 dígitos, tenta interpretar como CPF (padStart 11).
 * - Se tiver 11..14 dígitos, tenta interpretar como CNPJ (padStart 14).
 *
 * @param {unknown} value
 * @returns {{ originalDigits: string, cpfDigits: string|null, cnpjDigits: string|null }}
 */
export function normalizarCpfCnpj(value) {
  const originalDigits = onlyDigits(value);

  if (!originalDigits) {
    return { originalDigits: "", cpfDigits: null, cnpjDigits: null };
  }

  // Muito grande => inválido/inesperado
  if (originalDigits.length > 14) {
    return { originalDigits, cpfDigits: null, cnpjDigits: null };
  }

  const cpfDigits =
    originalDigits.length >= 9 && originalDigits.length <= 11
      ? padLeftZeros(originalDigits, 11)
      : null;

  const cnpjDigits =
    originalDigits.length >= 11 && originalDigits.length <= 14
      ? padLeftZeros(originalDigits, 14)
      : null;

  return { originalDigits, cpfDigits, cnpjDigits };
}

/**
 * Valida CPF aceitando casos de Excel removendo zeros à esquerda.
 * Aceita 9..11 dígitos e repõe zeros até 11 antes de validar.
 *
 * @param {string} cpf
 * @returns {boolean}
 */
export function validaCpf(cpf) {
  const digits = onlyDigits(cpf);
  if (digits.length < 9 || digits.length > 11) return false;
  return validaCpfDigits(padLeftZeros(digits, 11));
}

/**
 * Valida CNPJ aceitando casos de Excel removendo zeros à esquerda.
 * Aceita 11..14 dígitos e repõe zeros até 14 antes de validar.
 *
 * @param {string} cnpj
 * @returns {boolean}
 */
export function validaCnpj(cnpj) {
  const digits = onlyDigits(cnpj);
  if (digits.length < 11 || digits.length > 14) return false;
  return validaCnpjDigits(padLeftZeros(digits, 14));
}

/**
 * Valida CPF OU CNPJ (ideal para importação),
 * resolvendo ambiguidade comum do Excel:
 * - 11 dígitos pode ser CPF ou CNPJ com 3 zeros removidos.
 *
 * @param {unknown} value
 * @returns {{ valido: boolean, tipo: 'CPF'|'CNPJ'|null, digits: string, originalDigits: string }}
 */
export function validarCpfCnpj(value) {
  const { originalDigits, cpfDigits, cnpjDigits } = normalizarCpfCnpj(value);

  if (!originalDigits) {
    return { valido: false, tipo: null, digits: "", originalDigits: "" };
  }

  const cpfOk = cpfDigits ? validaCpfDigits(cpfDigits) : false;
  const cnpjOk = cnpjDigits ? validaCnpjDigits(cnpjDigits) : false;

  // Preferências:
  // - Se vier 14 => CNPJ
  // - Se vier 11 e CPF válido => CPF (mais comum)
  if (originalDigits.length === 14 && cnpjOk) {
    return { valido: true, tipo: "CNPJ", digits: cnpjDigits, originalDigits };
  }

  if (originalDigits.length === 11 && cpfOk) {
    return { valido: true, tipo: "CPF", digits: cpfDigits, originalDigits };
  }

  // Se só um validar, escolhe ele
  if (cpfOk && !cnpjOk) {
    return { valido: true, tipo: "CPF", digits: cpfDigits, originalDigits };
  }

  if (cnpjOk && !cpfOk) {
    return { valido: true, tipo: "CNPJ", digits: cnpjDigits, originalDigits };
  }

  // Se ambos validarem (raríssimo), escolhe por heurística
  if (cpfOk && cnpjOk) {
    const prefer = originalDigits.length <= 11 ? "CPF" : "CNPJ";
    return {
      valido: true,
      tipo: prefer,
      digits: prefer === "CPF" ? cpfDigits : cnpjDigits,
      originalDigits,
    };
  }

  // Nenhum validou: devolve fallback normalizado (útil para preview), mas marcado inválido.
  const fallbackDigits = cpfDigits ?? cnpjDigits ?? originalDigits;

  return { valido: false, tipo: null, digits: fallbackDigits, originalDigits };
}

export const verificarMaioridadePorData = (dataNascimento) => {
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;
  const diaAtual = hoje.getDate();

  const anoNascimento = dataNascimento.getFullYear();
  const mesNascimento = dataNascimento.getMonth() + 1;
  const diaNascimento = dataNascimento.getDate();

  let idade = anoAtual - anoNascimento;
  if (mesAtual < mesNascimento || (mesAtual === mesNascimento && diaAtual < diaNascimento)) idade--;

  return idade >= 18;
};

export const consultaCEP = async (cep, setFormData) => {
  try {
    const { data } = await Axios.get(`https://viacep.com.br/ws/${mask(cep, "99999-999")}/json/`);
    if (data.erro) {
      toast.error("CEP não encontrado");
      setFormData((prevData) => ({
        ...prevData,
        endereco_estado: "",
        endereco_cidade: "",
        endereco_rua: "",
        endereco_bairro: "",
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        endereco_estado: data.uf,
        endereco_cidade: data.localidade,
        endereco_rua: data.logradouro,
        endereco_bairro: data.bairro,
        endereco_numero: "",
        endereco_complemento: "",
      }));
      toast.success("CEP encontrado");
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error.response);
    toast.error("Serviço de verificação de CEP indisponível");
  }
};

export function valueValidation(info, valorfinal) {
  if (info) {
    const inferiorlimiteaux = info.map((num) => {
      return num[valorfinal];
    });
    const inferiorlimite = inferiorlimiteaux.sort(function (a, b) {
      return b - a;
    });
    if (!inferiorlimiteaux[0]) return 0;
    return inferiorlimite[0];
  }
  return 0;
}

/**
 * Normaliza retorno de erro vindo do Axios para exibição no front.
 *
 * Espera formato:
 * { mensagem: string, detalhes?: string|string[], req_id?: string }
 *
 * @param {any} err
 * @param {string} fallback
 * @returns {{ mensagem: string, detalhes: string[]|null, reqId: string|null }}
 */
export function normalizeApiError(err, fallback = "Ocorreu um erro ao processar a solicitação.") {
  const data = err?.response?.data;

  const mensagem = String(data?.mensagem || fallback);

  const rawDetalhes = data?.detalhes;
  const detalhes = Array.isArray(rawDetalhes)
    ? rawDetalhes.map(String).filter(Boolean)
    : typeof rawDetalhes === "string"
      ? [rawDetalhes]
      : null;

  const reqId = data?.req_id ? String(data.req_id) : null;

  return { mensagem, detalhes, reqId };
}

/**
 * Exibe erro de API no padrão correto (mensagem + detalhes).
 * Evita criar vários toasts, colocando os detalhes numa lista.
 *
 * @param {any} err
 * @param {string} fallback
 * @returns {void}
 */
export function toastApiError(err, fallback) {
  const { mensagem, detalhes, reqId } = normalizeApiError(err, fallback);

  if (detalhes?.length) {
    toast.error(
      <div>
        <div style={{ fontWeight: 700 }}>{mensagem}</div>
        <ul style={{ margin: "8px 0 0 18px" }}>
          {detalhes.slice(0, 10).map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      </div>,
      { autoClose: false }
    );
    return;
  }

  toast.error(reqId ? `${mensagem} (Protocolo: ${reqId})` : mensagem);
}
